import type {DesignerAttribution} from './types';
import type {AIPageGenerationState} from '@packages/systems/designer/ai-content-generation';
// Deep imports avoid package barrels that pull heavy runtime dependencies into broad tests:
// code-libraries/designer → code-mirror causes GuidGenerator TDZ crashes, and
// localization → JsonDomUtils initializes QualifiedMap in unrelated Designer Flux tests.
/* eslint-disable webflow/module-top-level-imports */
import {
  CODE_COMPONENT_CREATED,
  CODE_COMPONENT_UPDATED,
  CODE_EVALUATED,
} from '@packages/systems/code-libraries/designer/flux/actionTypes';
import {NODE_TRANSLATION_SUCCEEDED} from '@packages/systems/localization/flux/actions';
/* eslint-enable webflow/module-top-level-imports */
import {REMOTE_FILESYSTEM_FILES_COMMITTED} from '@packages/systems/designer/actions';
import {KNOWN_MCP_BRIDGE_NAMES} from '@packages/shared/constants/Attribution';
export type {DesignerAttribution} from './types';
export {KNOWN_MCP_BRIDGE_NAMES};

// VCC and code-component actions dispatched via the AI-driven VCC flow.
// These action types can also fire from non-AI paths (manual saves, builds, code-pane
// edits in the VCC pane), so this can over-attribute human VCC edits as WEBFLOW_AI.
// Over-attribution is the accepted tradeoff — VCC is an AI-first surface and missing
// AI attribution is worse than occasionally attributing a manual edit.
export const VCC_ACTIONS_ATTRIBUTED_AS_AI: ReadonlySet<string> = new Set([
  REMOTE_FILESYSTEM_FILES_COMMITTED,
  CODE_EVALUATED,
  CODE_COMPONENT_CREATED,
  CODE_COMPONENT_UPDATED,
]);

export const LOCALIZATION_TRANSLATION_ACTIONS_ATTRIBUTED_AS_AI: ReadonlySet<string> =
  new Set([NODE_TRANSLATION_SUCCEEDED]);

export const ATTRIBUTION_HEADER_SOURCE = 'X-Attribution-Source';
export const ATTRIBUTION_HEADER_AI_CONVERSATION_ID = 'X-AI-Conversation-Id';
export const ATTRIBUTION_HEADER_EXTENSION_APP_NAME = 'X-Extension-App-Name';

const isAISGHomePageStatusActive = (
  status: AIPageGenerationState['homePage']['status'] | undefined
): boolean => {
  switch (status) {
    case undefined:
    case 'idle':
    case 'done':
      return false;
    case 'generating':
    case 'inserting':
    case 'unlinking':
      return true;
    default: {
      const exhaustiveStatus: never = status;
      return exhaustiveStatus;
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let flux: any = null;

// Sync paths that can opt into pending AI attribution on outgoing requests.
// Generic request attribution intentionally cannot see pending flags.
export type AttributionSyncSource = 'dom' | 'styles';

// Set to true while an AI-generated suggestion is pending user acceptance/save.
// Existing accept flows treat this as a single-consumer token: the first
// source-tagged sync capture, RequestStore snapshot, or multiplayer diff
// snapshot consumes and clears it.
let _pendingAiAccept = false;

// Branch AI mutation paths can complete before legacy branch DomSync/CssSync
// requests are sent, and those requests can be debounced independently. Keep
// that debounce-spanning window scoped to branch sync instead of changing the
// lifetime of every accept flow.
let _pendingBranchAiAccept = false;
let _pendingBranchAiAcceptClearTimer: ReturnType<typeof setTimeout> | null =
  null;

const PENDING_BRANCH_AI_ACCEPT_SAFETY_TIMEOUT_MS = 45_000;

// Scoped attribution covers feature flows that mutate shared Designer state
// without a durable store signal at the exact sync/update boundary.
const activeAttributions: Array<{attribution: DesignerAttribution}> = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initDesignerAttribution(_flux: any): void {
  flux = _flux;
}

/**
 * Toggle the pending-AI-accept flag.
 */
export function setPendingAiAccept(value: boolean): void {
  _pendingAiAccept = value;
}

export function getPendingAiAccept(): boolean {
  return _pendingAiAccept;
}

export function setPendingBranchAiAccept(value: boolean): void {
  if (_pendingBranchAiAcceptClearTimer !== null) {
    clearTimeout(_pendingBranchAiAcceptClearTimer);
    _pendingBranchAiAcceptClearTimer = null;
  }

  if (!value) {
    _pendingBranchAiAccept = false;
    return;
  }

  _pendingBranchAiAccept = true;
  _pendingBranchAiAcceptClearTimer = setTimeout(() => {
    _pendingBranchAiAcceptClearTimer = null;
    _pendingBranchAiAccept = false;
  }, PENDING_BRANCH_AI_ACCEPT_SAFETY_TIMEOUT_MS);
}

/**
 * Runs callback with a scoped attribution. While the callback is executing,
 * getDesignerAttribution() returns this attribution instead of reading from
 * store state. When multiple async scopes overlap, the latest active
 * attribution wins — the most recently pushed entry takes priority.
 */
export async function withDesignerAttribution<T>(
  attribution: DesignerAttribution,
  callback: () => T | Promise<T>
): Promise<T> {
  const activeAttribution = {attribution};
  activeAttributions.push(activeAttribution);
  try {
    return await callback();
  } finally {
    const activeAttributionIndex =
      activeAttributions.lastIndexOf(activeAttribution);
    if (activeAttributionIndex !== -1) {
      activeAttributions.splice(activeAttributionIndex, 1);
    }
  }
}

export type StoreStateOverride = {
  AIAssistantStore?: {
    pendingExternalToolCall?: unknown;
    conversationId?: string | null;
  };
  AIPageGenerationStore?: Partial<
    Pick<AIPageGenerationState, 'homePage' | 'isNewPageGenerationInProgress'>
  >;
  DesignerStore?: {activeExtension?: {appId: string} | null};
  AppsStore?: {installedApps?: Array<{id: string; name: string}>};
  // Explicit snapshot override used by multiplayer diffing, NodeSync, and
  // tests without exposing the live module variable to generic request
  // attribution.
  AiAttributionStore?: {pendingAiAccept?: boolean};
};

export function getDesignerAttribution(
  storeState?: StoreStateOverride,
  actionType?: string
): DesignerAttribution | null {
  const activeAttribution =
    activeAttributions[activeAttributions.length - 1]?.attribution;
  if (activeAttribution) return activeAttribution;

  const useOverride = storeState !== undefined;

  // Explicit AI accept override for callers that opt into pending-AI
  // attribution for a specific state snapshot.
  //
  // IMPORTANT: the live module-level `_pendingAiAccept` flag is deliberately
  // NOT consulted here. That flag bridges the gap between an AI tool's
  // completion and later sync work, so it must only be observed by paths
  // that explicitly opt in: DomSync/CssSync via
  // `captureDesignerAttributionForRequest`, and multiplayer diffing/NodeSync
  // via `AiAttributionStore` snapshots.
  // Reading the flag from the default path would let it bleed into generic
  // header injection (AjaxRequest, ClientUtils) and stamp unrelated
  // Designer writes as WEBFLOW_AI.
  if (useOverride && storeState?.AiAttributionStore?.pendingAiAccept) {
    return {source: 'WEBFLOW_AI'};
  }

  if (!useOverride && !flux) return null;

  // AI Site Generation in progress.
  // Two signals: isNewPageGenerationInProgress (new-page flows, toggled by
  // aisgNewPageGenerationStarted/Finished) and homePage.status (homepage
  // generation, which has its own state machine). Either active means
  // WEBFLOW_AI attribution is needed. Prefer over-attribution to under-
  // attribution — a missed WEBFLOW_AI label on an AI mutation produces an
  // incorrect SAL record that is hard to retroactively fix.
  try {
    const aisgState = useOverride
      ? storeState.AIPageGenerationStore
      : flux?.getStoreState('AIPageGenerationStore');
    const homePageStatus = aisgState?.homePage?.status;
    if (
      aisgState?.isNewPageGenerationInProgress ||
      isAISGHomePageStatusActive(homePageStatus)
    ) {
      return {source: 'WEBFLOW_AI'};
    }
  } catch {
    // AIPageGenerationStore may not be registered in all contexts
  }

  // AI Assistant (AIA)
  try {
    const aiState = useOverride
      ? storeState.AIAssistantStore
      : flux?.getStoreState('AIAssistantStore');
    if (aiState?.pendingExternalToolCall && aiState?.conversationId) {
      return {
        source: 'WEBFLOW_AI',
        metadata: {aiConversationId: aiState.conversationId},
      };
    }
  } catch {
    // AIAssistantStore may not be registered in all contexts
  }

  // MCP Bridge (identified by app name)
  try {
    const designerState = useOverride
      ? storeState.DesignerStore
      : flux?.getStoreState('DesignerStore');
    const activeExtension = designerState?.activeExtension;
    if (activeExtension) {
      const appsState = useOverride
        ? storeState.AppsStore
        : flux?.getStoreState('AppsStore');
      const installedApps = appsState?.installedApps;
      const app = installedApps?.find?.(
        (a: {id: string; name: string}) => a.id === activeExtension.appId
      );
      const appName: string | undefined = app?.name;
      if (appName && KNOWN_MCP_BRIDGE_NAMES.has(appName)) {
        return {
          source: 'WEBFLOW_MCP',
          metadata: {
            extensionAppName: appName,
            extensionAppId: activeExtension.appId,
          },
        };
      }
    }
  } catch {
    // DesignerStore or AppsStore may not be registered in all contexts
  }

  const actionAttribution = getActionDesignerAttribution(actionType);
  if (actionAttribution) return actionAttribution;

  // Human action (includes non-MCP extensions)
  return null;
}

export function getActionDesignerAttribution(
  actionType?: string
): DesignerAttribution | null {
  // VCC action-type fallback (see VCC_ACTIONS_ATTRIBUTED_AS_AI comment above)
  if (actionType && VCC_ACTIONS_ATTRIBUTED_AS_AI.has(actionType)) {
    return {source: 'WEBFLOW_AI'};
  }

  if (
    actionType &&
    LOCALIZATION_TRANSLATION_ACTIONS_ATTRIBUTED_AS_AI.has(actionType)
  ) {
    return {source: 'WEBFLOW_AI'};
  }

  return null;
}

export function mergeDesignerAttribution(
  attribution: DesignerAttribution | null | undefined,
  fallbackAttribution: DesignerAttribution | null
): DesignerAttribution | null {
  if (attribution && fallbackAttribution?.source === attribution.source) {
    return {...fallbackAttribution, ...attribution};
  }

  return attribution ?? fallbackAttribution;
}

export interface CapturedDesignerAttribution {
  attribution: ReturnType<typeof getDesignerAttribution>;
  headers: ReturnType<typeof getAttributionHeaders>;
}

export type CaptureDesignerAttributionOptions = {
  isBranch?: boolean;
};

/**
 * Captures attribution from the current state for a single outgoing request.
 * Returns the attribution object (for request body), the corresponding
 * headers (derived from the same state snapshot to avoid TOCTOU
 * divergence).
 *
 * When `source` is provided and the live pending-AI-accept flag is active,
 * the capture is attributed to the AI and consumes the legacy token.
 *
 * Branch AI mutation paths use a separate timed flag that is only observed
 * by source-tagged branch sync captures. That lets DomSync and CssSync both
 * inherit WEBFLOW_AI after the original action-scoped AI signal clears,
 * without widening the pending window for non-branch accept flows.
 *
 * Use this in every path that sends a DOM or style sync request to the
 * server, in both the enhanced fetch path and the legacy jQuery path.
 *
 * Generic header injectors (AjaxRequest, ClientUtils) should NOT call
 * this — they get attribution from `getAttributionHeaders()`, which
 * deliberately does not see the live pending-AI-accept flag.
 */
export function captureDesignerAttributionForRequest(
  source?: AttributionSyncSource,
  options: CaptureDesignerAttributionOptions = {}
): CapturedDesignerAttribution {
  // Decide whether this capture attributes to the AI before computing the
  // attribution object — the flag is only consulted here, not in the
  // generic `getDesignerAttribution()` path.
  if (source !== undefined && options.isBranch && _pendingBranchAiAccept) {
    _pendingAiAccept = false;
    const attribution = {source: 'WEBFLOW_AI'} as const;
    return {
      attribution,
      headers: getAttributionHeadersForAttribution(attribution),
    };
  }

  if (_pendingAiAccept && source !== undefined) {
    _pendingAiAccept = false;
    const attribution = {source: 'WEBFLOW_AI'} as const;
    return {
      attribution,
      headers: getAttributionHeadersForAttribution(attribution),
    };
  }

  const attribution = getDesignerAttribution();
  return {
    attribution,
    headers: getAttributionHeadersForAttribution(attribution),
  };
}

export function getAttributionHeaders(
  storeState?: StoreStateOverride
): Record<string, string> {
  return getAttributionHeadersForAttribution(
    getDesignerAttribution(storeState)
  );
}

export function getAttributionHeadersForAttribution(
  attribution: DesignerAttribution | null
): Record<string, string> {
  if (!attribution) return {};
  const headers: Record<string, string> = {
    [ATTRIBUTION_HEADER_SOURCE]: attribution.source,
  };
  if (attribution.metadata?.aiConversationId) {
    headers[ATTRIBUTION_HEADER_AI_CONVERSATION_ID] =
      attribution.metadata.aiConversationId;
  }
  if (attribution.metadata?.extensionAppName) {
    headers[ATTRIBUTION_HEADER_EXTENSION_APP_NAME] =
      attribution.metadata.extensionAppName;
  }
  return headers;
}
